import remark from 'remark'
import remarkHtml from 'remark-html'
import merge from 'deepmerge'
// @ts-expect-error we just want the rules that are on this file and are not exported correctly
import { default as hastUtilSanitize } from 'hast-util-sanitize/lib/github'
import breaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'

import { headingToDiv } from './headingToDiv'
import { emoji } from './emoji'
import { brToSlashN } from './brToSlashN'
import { preserveEmptyLines } from './preserveEmptyLines'
import { ignoreCodeBlocks } from './ignoreCodeBlocks'
import { autoLinks } from './autolinks'
import { disableTokens } from './disableTokens'
import { ignoreBlockElements, unignoreBlockElements } from './blockElements'
import { ignoreNonOneOrderedLists } from './ignoreNonOneOrderedLists'

import type { AllowableFeatures } from './disableTokens'

export type MarkdownOptions = {
    headingsAsDiv?: boolean
    stripRootParagraphs?: boolean
    allowedFeatures?: AllowableFeatures
    maxHeadingDepth?: number
    smallHeaders?: boolean
    preserveEmptyLines?: boolean
    ignoreCodeBlocks?: boolean
    renderEmojis?: boolean
    ignoreNumberedListsNotStartingWithOne?: boolean
    normalizeLinkProtocols?: boolean
}

// Create a custom schema allowing class attributes on all elements
const sanitizeHtmlSchema = merge(hastUtilSanitize, {
    attributes: {
        '*': ['className'], // Allow class attribute on all elements
    },
})

export const markdownToHtml = (markdown: string, inline: boolean = false, options?: MarkdownOptions) => {
    const { ignoreNumberedListsNotStartingWithOne = true } = options || {}

    markdown = inline ? ignoreBlockElements(markdown) : markdown

    const shouldPreserveEmptyLines = !!options?.preserveEmptyLines

    let processor = remark()
        .use(brToSlashN)
        .use(preserveEmptyLines, { enabled: shouldPreserveEmptyLines })
        .use(ignoreCodeBlocks, { enabled: !!options?.ignoreCodeBlocks })
        .use(ignoreNonOneOrderedLists, { enabled: ignoreNumberedListsNotStartingWithOne, markdown })

    if (options?.renderEmojis) {
        processor = processor.use(emoji)
    }

    const { contents } = processor
        .use(breaks)
        .use(autoLinks, {
            target: '_blank',
            rel: ['noopener noreferrer'],
            normalizeProtocols: options?.normalizeLinkProtocols,
        })
        .use(remarkHtml, { sanitize: sanitizeHtmlSchema })
        .use(disableTokens, {
            allow: options?.allowedFeatures,
            maxHeadingDepth: options?.maxHeadingDepth,
        })
        .use(headingToDiv, {
            small: options?.smallHeaders,
            enabled: options?.headingsAsDiv !== false,
        })
        .use(remarkGfm)
        .processSync(markdown)

    let result = inline ? unignoreBlockElements(contents.toString()) : contents.toString()

    // we'll always strip the root <p> aways as it makes no sense
    if (options?.stripRootParagraphs !== false && result.startsWith('<p>')) {
        result = result.substring(3)
    }

    if (options?.stripRootParagraphs !== false && result.trim().endsWith('</p>')) {
        result = result.substring(0, result.lastIndexOf('</p>'))
    }

    if (!inline) {
        // HUGE workaround to correctly handle ordered lists (only rendered if not inline), so that it renders
        // the correct number if you start a list with i.e item #3
        result = result.replace(
            /<ol\b(?=[^>]*\bstart="(?!1\b)(\d+)")(?![^>]*\bstyle=")([^>]*)\bstart="(\d+)"/g,
            `<ol style="--start-value: $3" start="$3"$2`
        )
    }

    // enable the inputs
    result = result.replace(/(<input\b[^>]*?)\s*disabled(?:\s*=\s*["'][^"']*["'])?/gi, '$1')

    // if preserve empty lines we should now replace empty_text by &nbsp; so it renders an empty line
    if (shouldPreserveEmptyLines) {
        result = result.replace(/\|\|empty_text\|\|/g, '&nbsp;')
    } else if (inline) {
        // strip paragraphs that only contain non-breaking space placeholders
        result = result.replace(/<p>(?:\s|&nbsp;|&#160;)*<\/p>/g, '')
        // strip <br> elements that represent empty lines (standalone lines)
        result = result.replace(/^\s*<br\s*\/?>\s*$/gm, '')
    }

    return result
}
